import React from 'react'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { SelectProps } from '@radix-ui/react-select'
import { Field, FieldDescription, FieldError, FieldLabel } from '../ui/field'
import {
  InputBase,
  InputBaseAdornment,
  InputBaseControl,
  InputBaseInput,
} from '../ui/input-base'
import FormError, { ErrorProps } from './Error'

export type FormFieldWrapperProps = ErrorProps & {
  label?: string | React.ReactNode
  id?: string
  children?: React.ReactNode
  detail?: string | React.ReactNode
  detailError?: boolean
}

const FormFieldWrapper: React.FC<FormFieldWrapperProps> = ({
  label,
  id,
  detail,
  detailError,
  error,
  errorSize,
  children,
}) => {
  return (
    <Field>
      {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}
      {children}
      {detail && (
        <FieldDescription
          className={cn('text-right', detailError && 'text-destructive')}
        >
          {detail}
        </FieldDescription>
      )}
      {error && (
        <FieldError>
          <FormError error={error} errorSize={errorSize} />
        </FieldError>
      )}
    </Field>
  )
}

interface FormBaseProps extends FormFieldWrapperProps {
  append?: string | React.ReactNode
  prepend?: string | React.ReactNode
}

export interface FormInputProps
  extends React.ComponentProps<'input'>,
    FormBaseProps {}

const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      id,
      prepend,
      append,
      detail,
      detailError,
      name,
      error,
      errorSize,
      type = 'text',
      children,
      ...inputProps
    },
    ref,
  ) => {
    return (
      <FormFieldWrapper
        label={label}
        id={id}
        detail={detail}
        detailError={detailError}
        error={error}
        errorSize={errorSize}
      >
        <InputBase disabled={inputProps.disabled}>
          {prepend && <InputBaseAdornment>{prepend}</InputBaseAdornment>}
          <InputBaseControl>
            <InputBaseInput
              id={id}
              name={name}
              type={type}
              ref={ref}
              aria-invalid={!!error}
              {...inputProps}
            />
          </InputBaseControl>
          {append && <InputBaseAdornment>{append}</InputBaseAdornment>}
        </InputBase>
      </FormFieldWrapper>
    )
  },
)

FormInput.displayName = 'FormInput'

interface SelectItemOption {
  value: string
  label: string
}

interface FormSelectProps extends FormBaseProps, SelectProps {
  placeholder?: string
  className?: string
  id?: string
  items: SelectItemOption[]
}

const FormSelect = React.forwardRef<HTMLSelectElement, FormSelectProps>(
  (
    {
      label,
      id,
      placeholder,
      className,
      value,
      onValueChange,
      items,
      error,
      errorSize,
    },
    ref,
  ) => {
    const selected = items.find((item) => item.value === value)
    return (
      <FormFieldWrapper
        label={label}
        id={id}
        error={error}
        errorSize={errorSize}
      >
        <Select
          defaultValue={value}
          value={value}
          onValueChange={onValueChange}
        >
          <SelectTrigger className={className}>
            <SelectValue placeholder={placeholder}>
              {selected?.label}
            </SelectValue>
          </SelectTrigger>

          <SelectContent>
            <SelectGroup>
              {items.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </FormFieldWrapper>
    )
  },
)

FormSelect.displayName = 'FormSelect'

export { FormFieldWrapper, FormInput, FormSelect }
